import React, { useMemo } from 'react';

const colorMap = {
  green:  {
    stripe: 'bg-green-500',
    label:  'text-green-600 dark:text-green-400',
    bar:    'bg-green-500 dark:bg-green-400',
    track:  'bg-green-500/10 dark:bg-green-400/10',
  },
  indigo: {
    stripe: 'bg-brand-500',
    label:  'text-brand-600 dark:text-brand-400',
    bar:    'bg-brand-500 dark:bg-brand-400',
    track:  'bg-brand-500/10 dark:bg-brand-400/10',
  },
  amber:  {
    stripe: 'bg-amber-500',
    label:  'text-amber-600 dark:text-amber-400',
    bar:    'bg-amber-500 dark:bg-amber-400',
    track:  'bg-amber-500/10 dark:bg-amber-400/10',
  },
  blue:   {
    stripe: 'bg-blue-500',
    label:  'text-blue-600 dark:text-blue-400',
    bar:    'bg-blue-500 dark:bg-blue-400',
    track:  'bg-blue-500/10 dark:bg-blue-400/10',
  },
};

/**
 * Distribution strip: header + a single rounded card with N cells separated
 * by dividers. Each cell has a small colored accent dot, label, count, and
 * optional subtext.
 *
 * Two orientations:
 *   • "horizontal" (default): inline cells with vertical dividers from md+,
 *     mobile falls back to a 2-column grid.
 *   • "vertical": cells stacked top-to-bottom with horizontal dividers at
 *     all breakpoints — useful when paired side-by-side with a taller
 *     companion card.
 *
 * Optional per-rank breakdown: when a card includes `bars: [{ label, count }]`
 * AND orientation is "vertical", the cell renders a two-column body — left
 * text block (label / big count / descriptor) and a stacked list of
 * horizontal bars on the right, one per rank. Bars share a global x-scale
 * across all cells so a count of N is the same length everywhere.
 *
 * @param {{
 *   eyebrow?: React.ReactNode,
 *   headline?: React.ReactNode,
 *   cards: Array<{
 *     label: React.ReactNode,
 *     count: number | string,
 *     subtext?: React.ReactNode,
 *     accentColor?: 'green'|'indigo'|'amber'|'blue',
 *     bars?: Array<{ label: string, count: number }>,
 *   }>,
 *   orientation?: 'horizontal' | 'vertical',
 *   className?: string,
 * }} props
 */
export default function DistributionStripeCards({
  eyebrow,
  headline,
  description,
  cards = [],
  orientation = 'horizontal',
  className = '',
}) {
  const isVertical = orientation === 'vertical';

  // Shared y-scale across all bar groups: a "5" in one cell should look the
  // same height as a "5" in another. Falls back to 1 to avoid div-by-zero.
  const globalMaxBarCount = useMemo(() => {
    let max = 0;
    cards.forEach((c) => {
      if (Array.isArray(c?.bars)) {
        c.bars.forEach((b) => {
          const n = Number(b?.count) || 0;
          if (n > max) max = n;
        });
      }
    });
    return Math.max(max, 1);
  }, [cards]);

  if (!cards.length) return null;

  // Layout-only differences: vertical stacks cells with horizontal dividers
  // and gives each cell a touch more breathing room since the row is no
  // longer competing for horizontal space with siblings.
  const containerLayout = isVertical
    ? 'flex flex-col divide-y divide-x-0 divide-gray-100 dark:divide-gray-700'
    : 'grid grid-cols-2 md:flex divide-y divide-x-0 md:divide-y-0 md:divide-x divide-gray-100 dark:divide-gray-700';
  const cellPadding = isVertical ? 'px-5 py-3' : 'px-4 py-3';

  // In vertical orientation, allow the section + container to stretch so the
  // whole card matches the height of any taller sibling in the same grid row.
  const sectionLayout = isVertical ? 'h-full flex flex-col' : '';
  const containerStretch = isVertical ? 'flex-1' : '';
  const cellStretch = isVertical ? 'flex flex-col' : '';

  return (
    <section className={`${sectionLayout} ${className}`.trim()}>
      {(eyebrow || headline) ? (
        <header className="mb-4">
          {eyebrow ? (
            <div className="text-[11px] uppercase tracking-[0.22em] font-medium text-brand-600 dark:text-brand-400">
              {eyebrow}
            </div>
          ) : null}
          {headline ? (
            <h3 className="mt-1.5 text-base font-semibold tracking-tight text-slate-900 dark:text-white">
              {headline}
            </h3>
          ) : null}
          {description ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {description}
            </p>
          ) : null}
        </header>
      ) : null}
      <div
        className={`rounded-xl bg-surface dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 shadow-sm overflow-hidden ${containerStretch} ${containerLayout}`.trim()}
      >
        {cards.map((c, i) => {
          const colors = colorMap[c.accentColor] || colorMap.indigo;
          const showBars = isVertical && Array.isArray(c.bars) && c.bars.length > 0;
          const cellSum = showBars
            ? c.bars.reduce((a, b) => a + (Number(b?.count) || 0), 0)
            : 0;
          const peakBar = showBars
            ? c.bars.reduce((p, b) => ((Number(b?.count) || 0) > (Number(p?.count) || 0) ? b : p), null)
            : null;

          return (
            <div key={i} className={`flex-1 min-w-0 ${cellPadding} ${cellStretch}`.trim()}>
              {showBars ? (
                /* Two-column cell body: tight text block (left) + stacked
                   horizontal bars (right). Both columns vertically center so
                   the cell stays compact even when the card stretches to a
                   sibling's height. */
                <div className="flex items-center gap-5 lg:gap-6 h-full">
                  {/* LEFT: label + count + summary line (compact 3-row stack) */}
                  <div className="flex-shrink-0 w-28 lg:w-32">
                    <span className={`text-[11px] uppercase tracking-wider font-bold ${colors.label}`}>
                      {c.label}
                    </span>
                    <div className="mt-1 flex items-baseline gap-1.5">
                      <span className="text-2xl font-bold font-mono tabular-nums leading-none text-gray-900 dark:text-gray-100">
                        {c.count ?? 0}
                      </span>
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">
                        {Number(c.count ?? 0) === 1 ? 'day' : 'days'}
                      </span>
                    </div>
                    {c.subtext != null ? (
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-snug truncate">
                        {c.subtext}
                      </div>
                    ) : null}
                  </div>

                  {/* RIGHT: stacked horizontal bars, one per rank in the bin */}
                  <ul className="flex-1 flex flex-col justify-between gap-2 min-w-0 py-0.5" aria-label={`${c.label}: per-rank breakdown`}>
                    {c.bars.map((b, j) => {
                      const count = Number(b?.count) || 0;
                      const isZero = count === 0;
                      const widthPct = isZero
                        ? 0
                        : Math.max(3, (count / globalMaxBarCount) * 100);
                      const isPeak = peakBar && b.label === peakBar.label && count > 0;
                      return (
                        <li
                          key={`${b.label}-${j}`}
                          className="grid items-center gap-2.5"
                          style={{ gridTemplateColumns: '30px 1fr 26px' }}
                          title={`Rank ${b.label}: ${count} day${count === 1 ? '' : 's'}${
                            cellSum > 0 ? ` · ${Math.round((count / cellSum) * 100)}% of bin` : ''
                          }`}
                        >
                          <span
                            className={`text-[10px] font-mono tabular-nums tracking-wide font-medium ${
                              isZero ? 'text-gray-300 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'
                            }`}
                          >
                            {b.label}
                          </span>
                          <div className="h-[6px] rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700/50">
                            {!isZero ? (
                              <div
                                className={`h-full rounded-full ${colors.bar} transition-[width] duration-500 ease-out`}
                                style={{ width: `${widthPct}%` }}
                              />
                            ) : null}
                          </div>
                          <span
                            className={`text-[11px] font-mono tabular-nums text-right leading-none ${
                              isZero
                                ? 'text-gray-300 dark:text-gray-600'
                                : isPeak
                                  ? `font-bold ${colors.label}`
                                  : 'font-semibold text-gray-700 dark:text-gray-200'
                            }`}
                          >
                            {count}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                /* Fallback: legacy compact layout (no bars provided, or
                   horizontal orientation). */
                <>
                  <div>
                    <span className={`text-[11px] uppercase tracking-wider font-bold ${colors.label}`}>
                      {c.label}
                    </span>
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-2xl font-bold font-mono tabular-nums text-gray-900 dark:text-gray-100">
                      {c.count ?? 0}
                    </span>
                    {c.subtext != null ? (
                      <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{c.subtext}</span>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
